import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';

export const isReaderPath = (pathname: string) =>
  pathname === '/read' || pathname.startsWith('/read/');

const MobileOnly = styled.div`
  @media only screen and (min-width: 768px) {
    display: none;
  }
`;

const Navigation = styled.nav`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 150;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  padding: 6px 8px max(6px, env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.navigation.border};
  background: ${({ theme }) => theme.navigation.background};
  box-shadow: 0 -8px 24px ${({ theme }) => theme.navigation.shadow};
  backdrop-filter: blur(14px);
`;

const NavigationButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 5px 2px;
  border: 0;
  border-radius: 12px;
  background: ${({ $active, theme }) =>
    $active ? theme.navigation.activeBackground : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.navigation.activeText : theme.navigation.text};
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  line-height: 1.1;

  > .icon {
    height: 19px;
    margin: 0 !important;
    font-size: 18px;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.navigation.focus};
    outline-offset: 2px;
  }
`;

const Scrim = styled.button`
  position: fixed;
  inset: 0;
  z-index: 151;
  width: 100%;
  border: 0;
  background: ${({ theme }) => theme.navigation.scrim};
  cursor: default;
`;

const MoreSheet = styled.section`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 152;
  max-height: min(620px, calc(100vh - 36px));
  overflow-y: auto;
  padding: 10px 16px max(20px, env(safe-area-inset-bottom));
  border-radius: 24px 24px 0 0;
  background: ${({ theme }) => theme.navigation.surface};
  box-shadow: 0 -16px 40px ${({ theme }) => theme.navigation.raisedShadow};
`;

const SheetHandle = styled.div`
  width: 38px;
  height: 4px;
  margin: 0 auto 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.navigation.handle};
`;

const SheetTitle = styled.h2`
  margin: 0 4px 8px;
  color: ${({ theme }) => theme.navigation.heading};
  font-size: 16px;
`;

const SheetHeader = styled.div`
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
`;

const SheetClose = styled.button`
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.navigation.raisedSurface};
  color: ${({ theme }) => theme.navigation.mutedText};
  cursor: pointer;

  > .icon {
    margin: 0 !important;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.navigation.focus};
    outline-offset: 2px;
  }
`;

const SheetLink = styled.button`
  display: flex;
  width: 100%;
  min-height: 52px;
  align-items: center;
  gap: 14px;
  padding: 10px 8px;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.navigation.divider};
  background: transparent;
  color: ${({ theme }) => theme.font.primary};
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  text-align: left;

  > .icon {
    width: 18px;
    margin: 0 !important;
    color: ${({ theme }) => theme.navigation.accent};
    font-size: 17px;
    text-align: center;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.navigation.focus};
    outline-offset: -3px;
  }
`;

const isDiscoverPath = (pathname: string) => pathname.startsWith('/discover');
const isInboxPath = (pathname: string) => pathname.startsWith('/inbox');

const MobileNavigation: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreCloseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setMoreOpen(false), [pathname]);

  useEffect(() => {
    if (!moreOpen) {
      return undefined;
    }
    moreCloseRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMoreOpen(false);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [moreOpen]);

  if (!userProfile || isReaderPath(pathname)) {
    return null;
  }

  const goTo = (to: string) => {
    setMoreOpen(false);
    history.push(to);
  };

  const moreItems = [
    { icon: 'users', label: t('ReaderList'), to: '/readers' },
    { icon: 'bookmark', label: t('SampleChapters'), to: '/sample-chapters' },
    { icon: 'feed', label: t('Feed'), to: '/feed' },
    { icon: 'comments', label: t('CritiqueGroups'), to: '/critique-groups' },
    { icon: 'bell', label: t('Notifications'), to: '/notifications' },
    { icon: 'user', label: t('Account'), to: '/account/profile' },
    {
      icon: 'help circle',
      label: t('Support'),
      to: '/inbox/conversations/support',
    },
  ] as const;

  return (
    <MobileOnly>
      {moreOpen && (
        <>
          <Scrim
            type="button"
            aria-label={t('Close')}
            onClick={() => setMoreOpen(false)}
          />
          <MoreSheet
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
          >
            <SheetHandle aria-hidden="true" />
            <SheetHeader>
              <SheetTitle id="mobile-more-title">{t('More')}</SheetTitle>
              <SheetClose
                ref={moreCloseRef}
                type="button"
                aria-label={t('Close')}
                onClick={() => setMoreOpen(false)}
              >
                <Icon name="close" />
              </SheetClose>
            </SheetHeader>
            {moreItems.map(item => (
              <SheetLink
                key={item.to}
                type="button"
                onClick={() => goTo(item.to)}
              >
                <Icon name={item.icon} />
                {item.label}
              </SheetLink>
            ))}
          </MoreSheet>
        </>
      )}
      <Navigation aria-label={t('MainNavigation', 'Main navigation')}>
        <NavigationButton
          type="button"
          $active={pathname === '/mymanuscripts'}
          aria-current={pathname === '/mymanuscripts' ? 'page' : undefined}
          onClick={() => goTo('/mymanuscripts')}
        >
          <Icon name="book" />
          {t('Library')}
        </NavigationButton>
        <NavigationButton
          type="button"
          $active={pathname === '/readinglist'}
          aria-current={pathname === '/readinglist' ? 'page' : undefined}
          onClick={() => goTo('/readinglist')}
        >
          <Icon name="bookmark" />
          {t('ReadingList')}
        </NavigationButton>
        <NavigationButton
          type="button"
          $active={isDiscoverPath(pathname)}
          aria-current={isDiscoverPath(pathname) ? 'page' : undefined}
          onClick={() => goTo('/discover')}
        >
          <Icon name="compass outline" />
          {t('Discover')}
        </NavigationButton>
        <NavigationButton
          type="button"
          $active={isInboxPath(pathname)}
          aria-current={isInboxPath(pathname) ? 'page' : undefined}
          onClick={() => goTo('/inbox')}
        >
          <Icon name="comments outline" />
          {t('Chat')}
        </NavigationButton>
        <NavigationButton
          type="button"
          $active={moreOpen}
          aria-expanded={moreOpen}
          aria-controls="mobile-more-title"
          onClick={() => setMoreOpen(open => !open)}
        >
          <Icon name="th large" />
          {t('More')}
        </NavigationButton>
      </Navigation>
    </MobileOnly>
  );
};

export default MobileNavigation;
